import {
  BANNER_SPEND_BANDS,
  BANNER_SUBTITLE_MAX,
  BANNER_TITLE_MAX,
  type BannerSpendBand,
  bannerState,
} from "@loot-os/api-contract";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@loot-os/ui/components/alert-dialog";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import { Checkbox } from "@loot-os/ui/components/checkbox";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@loot-os/ui/components/field";
import { Input } from "@loot-os/ui/components/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@loot-os/ui/components/sheet";
import { toast } from "@loot-os/ui/components/sonner";
import { Switch } from "@loot-os/ui/components/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@loot-os/ui/components/tabs";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import { useCurrency } from "../lib/currency";
import {
  BANNER_STATE_LABELS,
  type BannerDraft,
  bannerDraftFrom,
  bannerDraftIssues,
  bannerDraftToInput,
  hasNoBannerIssues,
  moveItem,
  newSlideDraft,
  type SegmentLadder,
  type SlideDraft,
  type SlideDraftIssues,
  type StorefrontBanner,
  slideHasArt,
  spendBandDescription,
  spendBandLabel,
} from "../lib/storefrontBanners";
import { uploadTenantAssetFile } from "../lib/tenantAssets";
import { useTRPC } from "../lib/trpc";
import { DayTimeFields } from "./DayTimeFields";
import { RasterImageField } from "./StorefrontFields";

type SlideImageField = "desktopImageUrl" | "mobileImageUrl";

/**
 * The banner editor, in a sheet: name and switch, when it runs, who sees it, and its slides. A
 * viewer opens it read-only.
 */
export function StorefrontBannerSheet({
  editorKey,
  banner,
  open,
  onOpenChange,
  readOnly,
  maxSlides,
  segments,
  uploadsAvailable,
  visitorsSeeGames,
}: {
  /** Changes on every open, so each opens on a fresh draft; null before the first. */
  editorKey: number | null;
  /** The banner opened, or null for a new one. */
  banner: StorefrontBanner | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  readOnly: boolean;
  maxSlides: number;
  segments: SegmentLadder;
  uploadsAvailable: boolean;
  /** The store's Home shows a visitor its games in the banners' place. */
  visitorsSeeGames: boolean;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="gap-0 overflow-y-auto data-[side=right]:w-full data-[side=right]:sm:max-w-3xl">
        {editorKey !== null && (
          <BannerEditor
            key={editorKey}
            banner={banner}
            readOnly={readOnly}
            maxSlides={maxSlides}
            segments={segments}
            uploadsAvailable={uploadsAvailable}
            visitorsSeeGames={visitorsSeeGames}
            onDone={() => onOpenChange(false)}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function BannerEditor({
  banner,
  readOnly,
  maxSlides,
  segments,
  uploadsAvailable,
  visitorsSeeGames,
  onDone,
}: {
  banner: StorefrontBanner | null;
  readOnly: boolean;
  maxSlides: number;
  segments: SegmentLadder;
  uploadsAvailable: boolean;
  visitorsSeeGames: boolean;
  onDone: () => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const currency = useCurrency();
  const [now] = useState(() => new Date());
  const [draft, setDraft] = useState<BannerDraft>(() => bannerDraftFrom(banner, now));
  const [attempted, setAttempted] = useState(false);
  // Which image an upload is running for (`{slide key}:{field}`), from the pick until the check.
  const [uploading, setUploading] = useState<string | null>(null);

  const create = useMutation(trpc.storefrontBanners.create.mutationOptions());
  const update = useMutation(trpc.storefrontBanners.update.mutationOptions());
  const startUpload = useMutation(trpc.storefrontBanners.startImageUpload.mutationOptions());
  const finishUpload = useMutation(trpc.storefrontBanners.finishImageUpload.mutationOptions());

  const issues = bannerDraftIssues(draft, maxSlides);
  const saving = create.isPending || update.isPending;
  const disabled = readOnly || saving;
  const shown = (issue: string | undefined) => (attempted ? issue : undefined);

  const setSlide = (key: string, patch: Partial<SlideDraft>) =>
    setDraft((d) => ({
      ...d,
      slides: d.slides.map((slide) => (slide.key === key ? { ...slide, ...patch } : slide)),
    }));

  const upload = async (key: string, field: SlideImageField, file: File) => {
    setUploading(`${key}:${field}`);
    try {
      const { publicUrl } = await uploadTenantAssetFile(
        file,
        (input) => startUpload.mutateAsync(input),
        (uploadKey) => finishUpload.mutateAsync({ uploadKey }),
      );
      setSlide(key, { [field]: publicUrl });
    } finally {
      setUploading(null);
    }
  };

  const submit = () => {
    setAttempted(true);
    if (!hasNoBannerIssues(issues)) return;
    const input = bannerDraftToInput(draft);
    const onSuccess = () => {
      void queryClient.invalidateQueries({ queryKey: trpc.storefrontBanners.list.pathKey() });
      toast.success(banner ? "Banner saved." : "Banner added.");
      onDone();
    };
    if (banner) update.mutate({ bannerId: banner.id, banner: input }, { onSuccess });
    else create.mutate(input, { onSuccess });
  };

  const toggleBand = (band: BannerSpendBand, checked: boolean) =>
    setDraft((d) => ({
      ...d,
      spendBands: checked
        ? [...d.spendBands.filter((b) => b !== band), band]
        : d.spendBands.filter((b) => b !== band),
    }));

  const state = banner ? bannerState(banner, now) : null;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <form
      className="flex min-h-full flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <SheetHeader className="border-b pr-12">
        <div className="flex flex-wrap items-center gap-2">
          <SheetTitle className="text-base">
            {readOnly ? (banner?.name ?? "Banner") : banner ? "Edit banner" : "New banner"}
          </SheetTitle>
          {state && <Badge variant="outline">{BANNER_STATE_LABELS[state]}</Badge>}
        </div>
        <SheetDescription>
          A set of slides the storefront shows while the banner runs, to the shoppers it names.
        </SheetDescription>
      </SheetHeader>

      <div className="grid flex-1 content-start gap-8 p-4">
        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
          <Field data-invalid={shown(issues.name) ? true : undefined}>
            <FieldLabel htmlFor="banner-name">Name</FieldLabel>
            <Input
              id="banner-name"
              value={draft.name}
              disabled={disabled}
              placeholder="Autumn drop"
              aria-invalid={shown(issues.name) ? true : undefined}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
            {shown(issues.name) ? (
              <FieldError>{issues.name}</FieldError>
            ) : (
              <FieldDescription>For you and your team; shoppers never see it.</FieldDescription>
            )}
          </Field>
          <Field orientation="horizontal" className="sm:pt-7">
            <Switch
              id="banner-enabled"
              checked={draft.enabled}
              disabled={disabled}
              onCheckedChange={(enabled) => setDraft((d) => ({ ...d, enabled }))}
            />
            <FieldLabel htmlFor="banner-enabled">{draft.enabled ? "On" : "Paused"}</FieldLabel>
          </Field>
        </div>

        <FieldSet>
          <FieldLegend variant="label">When it runs</FieldLegend>
          <FieldDescription>
            {`In your time zone, ${timeZone}. A paused banner never shows and doesn't count toward the banners live at once.`}
          </FieldDescription>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="grid content-start gap-3">
              <p className="text-sm font-medium">Starts</p>
              <Tabs
                value={draft.startsNow ? "now" : "later"}
                onValueChange={(value: "now" | "later") =>
                  setDraft((d) => ({ ...d, startsNow: value === "now" }))
                }
              >
                <TabsList>
                  <TabsTrigger value="now" disabled={disabled}>
                    Right away
                  </TabsTrigger>
                  <TabsTrigger value="later" disabled={disabled}>
                    At a time
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="later" className="grid gap-2">
                  <DayTimeFields
                    id="banner-starts"
                    value={draft.starts}
                    disabled={disabled}
                    onChange={(starts) => setDraft((d) => ({ ...d, starts }))}
                  />
                  {shown(issues.starts) && <FieldError>{issues.starts}</FieldError>}
                </TabsContent>
              </Tabs>
            </div>
            <div className="grid content-start gap-3">
              <p className="text-sm font-medium">Ends</p>
              <Tabs
                value={draft.endless ? "never" : "later"}
                onValueChange={(value: "never" | "later") =>
                  setDraft((d) => ({ ...d, endless: value === "never" }))
                }
              >
                <TabsList>
                  <TabsTrigger value="never" disabled={disabled}>
                    No end
                  </TabsTrigger>
                  <TabsTrigger value="later" disabled={disabled}>
                    At a time
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="later" className="grid gap-2">
                  <DayTimeFields
                    id="banner-ends"
                    value={draft.ends}
                    disabled={disabled}
                    onChange={(ends) => setDraft((d) => ({ ...d, ends }))}
                  />
                  {shown(issues.ends) && <FieldError>{issues.ends}</FieldError>}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </FieldSet>

        <FieldSet>
          <FieldLegend variant="label">Shown to</FieldLegend>
          <FieldDescription>
            Spend bands go by what a shopper spent lately. Tick none to show it to everyone.
          </FieldDescription>
          <div className="grid gap-3 sm:grid-cols-2">
            {BANNER_SPEND_BANDS.map((band) => (
              <Field key={band} orientation="horizontal" className="items-start">
                <Checkbox
                  id={`banner-band-${band}`}
                  checked={draft.spendBands.includes(band)}
                  disabled={disabled}
                  onCheckedChange={(checked) => toggleBand(band, checked === true)}
                />
                <div className="grid gap-0.5">
                  <FieldLabel htmlFor={`banner-band-${band}`}>{spendBandLabel(band)}</FieldLabel>
                  <FieldDescription>
                    {spendBandDescription(band, segments, currency, visitorsSeeGames)}
                  </FieldDescription>
                </div>
              </Field>
            ))}
          </div>
        </FieldSet>

        <FieldSet>
          <FieldLegend variant="label">Slides</FieldLegend>
          <FieldDescription>
            {`Shown in this order, up to ${maxSlides}, in Home's carousel. A slide's title and subtitle sit on the store's colors over the left of its art, with its button under them, so keep what matters in the art to the right; on a phone they cover most of it. Wide art shows on screens 768px and up, phone art below that; each fills its frame, cropped to fit. A slide with a title can skip the art, and then shows on the store's own colors.`}
            {!uploadsAvailable && " Uploads aren't available on this stage; paste image URLs."}
          </FieldDescription>
          {shown(issues.slides) && <FieldError>{issues.slides}</FieldError>}
          <div className="grid gap-4">
            {draft.slides.map((slide, index) => (
              <SlideFields
                key={slide.key}
                slide={slide}
                index={index}
                count={draft.slides.length}
                issues={attempted ? (issues.slideIssues[index] ?? {}) : {}}
                disabled={disabled}
                uploading={uploading}
                uploadsAvailable={uploadsAvailable}
                onChange={(patch) => setSlide(slide.key, patch)}
                onFile={(field, file) => upload(slide.key, field, file)}
                onMove={(direction) =>
                  setDraft((d) => ({ ...d, slides: moveItem(d.slides, index, direction) }))
                }
                onRemove={() =>
                  setDraft((d) => ({ ...d, slides: d.slides.filter((s) => s.key !== slide.key) }))
                }
              />
            ))}
          </div>
          {!readOnly && (
            <Button
              type="button"
              variant="outline"
              className="justify-self-start"
              disabled={disabled || draft.slides.length >= maxSlides}
              onClick={() => setDraft((d) => ({ ...d, slides: [...d.slides, newSlideDraft()] }))}
            >
              <Plus />
              Add slide
            </Button>
          )}
        </FieldSet>
      </div>

      {!readOnly && (
        <SheetFooter className="sticky bottom-0 flex-row flex-wrap items-center gap-2 border-t bg-popover">
          <Button type="submit" disabled={saving || uploading !== null}>
            {saving ? "Saving…" : banner ? "Save changes" : "Add banner"}
          </Button>
          <Button type="button" variant="outline" disabled={saving} onClick={onDone}>
            Cancel
          </Button>
          {banner && (
            <div className="sm:ml-auto">
              <DeleteStorefrontBannerButton bannerId={banner.id} onDeleted={onDone} />
            </div>
          )}
        </SheetFooter>
      )}
    </form>
  );
}

/**
 * One slide: its title and subtitle, its optional art and the art's alt text, and its optional
 * button, with its place in the order.
 */
function SlideFields({
  slide,
  index,
  count,
  issues,
  disabled,
  uploading,
  uploadsAvailable,
  onChange,
  onFile,
  onMove,
  onRemove,
}: {
  slide: SlideDraft;
  index: number;
  count: number;
  issues: SlideDraftIssues;
  disabled: boolean;
  uploading: string | null;
  uploadsAvailable: boolean;
  onChange: (patch: Partial<SlideDraft>) => void;
  onFile: (field: SlideImageField, file: File) => Promise<void>;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}) {
  const id = `banner-slide-${slide.key}`;
  const hasArt = slideHasArt(slide);
  return (
    <div className="grid gap-4 rounded-lg border p-4">
      <div className="flex items-center gap-1">
        <p className="mr-auto text-sm font-medium">{`Slide ${index + 1}`}</p>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={`Move slide ${index + 1} up`}
          disabled={disabled || index === 0}
          onClick={() => onMove(-1)}
        >
          <ArrowUp />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={`Move slide ${index + 1} down`}
          disabled={disabled || index === count - 1}
          onClick={() => onMove(1)}
        >
          <ArrowDown />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={`Remove slide ${index + 1}`}
          disabled={disabled || count === 1}
          onClick={onRemove}
        >
          <Trash2 />
        </Button>
      </div>

      <Field data-invalid={issues.title ? true : undefined}>
        <FieldLabel htmlFor={`${id}-title`}>Title</FieldLabel>
        <Input
          id={`${id}-title`}
          value={slide.title}
          disabled={disabled}
          placeholder="Battle for the best pulls"
          aria-invalid={issues.title ? true : undefined}
          onChange={(e) => onChange({ title: e.target.value })}
        />
        {issues.title ? (
          <FieldError>{issues.title}</FieldError>
        ) : (
          <FieldDescription>
            {`Optional. Shown large over the art, or on its own. At most ${BANNER_TITLE_MAX} characters.`}
          </FieldDescription>
        )}
      </Field>

      <Field data-invalid={issues.subtitle ? true : undefined}>
        <FieldLabel htmlFor={`${id}-subtitle`}>Subtitle</FieldLabel>
        <Input
          id={`${id}-subtitle`}
          value={slide.subtitle}
          disabled={disabled}
          placeholder="Open packs head to head with other players."
          aria-invalid={issues.subtitle ? true : undefined}
          onChange={(e) => onChange({ subtitle: e.target.value })}
        />
        {issues.subtitle ? (
          <FieldError>{issues.subtitle}</FieldError>
        ) : (
          <FieldDescription>
            {`Optional. A line under the title. At most ${BANNER_SUBTITLE_MAX} characters.`}
          </FieldDescription>
        )}
      </Field>

      <div className="grid gap-2">
        <div className="grid gap-4 sm:grid-cols-2">
          {(
            [
              ["desktopImageUrl", "Wide art", "Screens 768px and up. 4.5:1, e.g. 2880 × 640."],
              ["mobileImageUrl", "Phone art", "Screens under 768px. 2:1, e.g. 1200 × 600."],
            ] as const
          ).map(([field, label, where]) => (
            <div key={field} className="grid gap-1">
              <RasterImageField
                id={`${id}-${field}`}
                label={label}
                url={slide[field]}
                onUrlChange={(url) => onChange({ [field]: url })}
                onFile={(file) => onFile(field, file)}
                uploading={uploading === `${slide.key}:${field}`}
                disabled={disabled}
                uploadsAvailable={uploadsAvailable}
                previewClassName="size-full object-cover"
                help={`Optional with a title. ${where} PNG, JPEG, WEBP or AVIF, up to 5 MB — not SVG.`}
              />
              {issues[field] && <FieldError>{issues[field]}</FieldError>}
            </div>
          ))}
        </div>
        {/* Not while one of the slide's uploads runs: it would write its URL back when done. */}
        {hasArt && !disabled && !uploading?.startsWith(`${slide.key}:`) && (
          <div>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              aria-label={`Remove art from slide ${index + 1}`}
              onClick={() => onChange({ desktopImageUrl: "", mobileImageUrl: "", altText: "" })}
            >
              Remove art
            </Button>
          </div>
        )}
      </div>

      {hasArt && (
        <Field data-invalid={issues.altText ? true : undefined}>
          <FieldLabel htmlFor={`${id}-alt`}>Alt text</FieldLabel>
          <Input
            id={`${id}-alt`}
            value={slide.altText}
            disabled={disabled}
            placeholder="What the art shows"
            aria-invalid={issues.altText ? true : undefined}
            onChange={(e) => onChange({ altText: e.target.value })}
          />
          {issues.altText ? (
            <FieldError>{issues.altText}</FieldError>
          ) : (
            <FieldDescription>
              Read aloud by screen readers. Leave it empty for pure decoration, or when the title
              already says it.
            </FieldDescription>
          )}
        </Field>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field data-invalid={issues.action ? true : undefined}>
          <FieldLabel htmlFor={`${id}-action-label`}>Button label</FieldLabel>
          <Input
            id={`${id}-action-label`}
            value={slide.actionLabel}
            disabled={disabled}
            placeholder="Shop now"
            onChange={(e) => onChange({ actionLabel: e.target.value })}
          />
        </Field>
        <Field data-invalid={issues.action ? true : undefined}>
          <FieldLabel htmlFor={`${id}-action-url`}>Button link</FieldLabel>
          <Input
            id={`${id}-action-url`}
            value={slide.actionUrl}
            disabled={disabled}
            placeholder="/boxes"
            onChange={(e) => onChange({ actionUrl: e.target.value })}
          />
        </Field>
      </div>
      {issues.action ? (
        <FieldError>{issues.action}</FieldError>
      ) : (
        <p className="-mt-2 text-xs text-muted-foreground">
          Optional. A storefront path like /boxes, or an https:// link.
        </p>
      )}
    </div>
  );
}

/** Deletes a banner after a confirmation. */
export function DeleteStorefrontBannerButton({
  bannerId,
  size = "default",
  onDeleted,
}: {
  bannerId: string;
  size?: "default" | "sm";
  onDeleted?: () => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const remove = useMutation(
    trpc.storefrontBanners.delete.mutationOptions({
      onSuccess: () => {
        toast.success("Banner deleted.");
        setOpen(false);
        onDeleted?.();
        void queryClient.invalidateQueries({ queryKey: trpc.storefrontBanners.list.pathKey() });
      },
    }),
  );

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger render={<Button type="button" variant="destructive" size={size} />}>
        Delete
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this banner?</AlertDialogTitle>
          <AlertDialogDescription>
            The storefront stops showing it at once, and it can't be brought back. To take it down
            for now, pause it instead.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={remove.isPending}
            onClick={() => remove.mutate({ bannerId })}
          >
            {remove.isPending ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
